// Fonctions IA (OpenRouter) : factures, courriels, lecture de reçus et de factures.
// Par défaut, les appels passent par le serveur (/papi/ai/chat) qui détient la clé OpenRouter.
// Une clé personnelle saisie dans Outils IA (localStorage) prend le dessus et appelle OpenRouter directement.
import { api } from './api'

const OPENROUTER_URL = 'https://openrouter.ai/api/v1/chat/completions'
const LS_KEY = 'pivot_openrouter_key'
const LS_MODEL = 'pivot_openrouter_model'
export const DEFAULT_MODEL = 'anthropic/claude-haiku-4.5'

/** Extrait un objet JSON même s'il est entouré de texte ou de fences markdown ```json. */
function parseJsonLoose<T>(raw: string): T {
  try {
    return JSON.parse(raw) as T
  } catch {
    const m = raw.match(/\{[\s\S]*\}/)
    if (m) return JSON.parse(m[0]) as T
    throw new Error('Réponse IA non parsable : ' + raw.slice(0, 120))
  }
}

export function getOpenRouterKey(): string {
  return localStorage.getItem(LS_KEY) || ''
}
export function setOpenRouterKey(k: string) {
  localStorage.setItem(LS_KEY, k.trim())
}
export function getModel(): string {
  return localStorage.getItem(LS_MODEL) || DEFAULT_MODEL
}
export function setModel(m: string) {
  localStorage.setItem(LS_MODEL, m.trim())
}

export interface AiModel { id: string; name: string; context: number; vision: boolean; prompt: number; completion: number }
/** Modèles disponibles via la clé du serveur (liste OpenRouter, cache 1 h côté serveur). */
export async function listModels(): Promise<{ configured: boolean; models: AiModel[] }> {
  return api('/ai/models')
}

type Content = string | { type: string; text?: string; image_url?: { url: string }; file?: { filename: string; file_data: string } }[]
/** Complétion : relais serveur (clé serveur) ou OpenRouter direct si une clé personnelle est saisie. */
async function chat(messages: { role: string; content: Content }[], opts?: { json?: boolean; temperature?: number }): Promise<string> {
  const body = { model: getModel(), messages, temperature: opts?.temperature ?? 0.4, ...(opts?.json ? { response_format: { type: 'json_object' } } : {}) }
  const key = getOpenRouterKey()
  if (!key) {
    const r = await api<{ content: string }>('/ai/chat', { method: 'POST', json: body })
    return r.content || ''
  }
  const res = await fetch(OPENROUTER_URL, {
    method: 'POST',
    headers: { Authorization: `Bearer ${key}`, 'Content-Type': 'application/json', 'HTTP-Referer': 'https://pivot-media.ca', 'X-Title': 'Pivot Média' },
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error(`OpenRouter ${res.status}: ${(await res.text()).slice(0, 300)}`)
  const data = await res.json()
  return data.choices?.[0]?.message?.content ?? ''
}

// ---------- Taxes Québec (calculées en code, jamais par l'IA) ----------
export const TPS_RATE = 0.05 // TPS / GST
export const TVQ_RATE = 0.09975 // TVQ / QST

export interface InvoiceItem {
  description: string
  quantity: number
  unit_price: number
}
export interface InvoiceDraft {
  items: InvoiceItem[]
  notes: string
  subtotal: number
  tps: number
  tvq: number
  total: number
}

function round2(n: number) {
  return Math.round(n * 100) / 100
}

export function computeTotals(items: InvoiceItem[]) {
  const subtotal = round2(items.reduce((s, it) => s + it.quantity * it.unit_price, 0))
  const tps = round2(subtotal * TPS_RATE)
  const tvq = round2(subtotal * TVQ_RATE)
  const total = round2(subtotal + tps + tvq)
  return { subtotal, tps, tvq, total }
}

/** Génère les lignes d'une facture à partir d'une description libre du projet/mandat. */
export async function generateInvoice(prompt: string): Promise<InvoiceDraft> {
  const sys =
    "Tu es assistant facturation pour Pivot Média, une boîte de production vidéo au Québec. " +
    "À partir de la description du mandat, produis des lignes de facture claires et professionnelles en français. " +
    "Retourne UNIQUEMENT du JSON valide de la forme " +
    '{"items":[{"description":string,"quantity":number,"unit_price":number}],"notes":string}. ' +
    "unit_price est en dollars canadiens, avant taxes. N'invente pas de taxes (elles sont ajoutées séparément). " +
    'Sois réaliste sur les prix de production vidéo (tournage, montage, drone, etc.).'
  const raw = await chat(
    [
      { role: 'system', content: sys },
      { role: 'user', content: prompt },
    ],
    { json: true }
  )
  const parsed = parseJsonLoose<{ items: InvoiceItem[]; notes?: string }>(raw)
  const items = (parsed.items || []).map((it) => ({
    description: String(it.description || '').trim(),
    quantity: Number(it.quantity) || 1,
    unit_price: Number(it.unit_price) || 0,
  }))
  const totals = computeTotals(items)
  return { items, notes: parsed.notes || '', ...totals }
}

/** Génère un courriel client (sujet + corps) à partir d'une intention. */
export async function generateEmail(context: {
  intention: string
  clientName?: string
  projet?: string
}): Promise<{ subject: string; body: string }> {
  const sys =
    "Tu rédiges des courriels professionnels en français québécois pour Pivot Média (production vidéo, Québec). " +
    "Ton: chaleureux, clair, concis, jamais insistant. Signe toujours « — Pivot Média ». " +
    "Ne mentionne jamais de prix précis dans le courriel. " +
    'Retourne UNIQUEMENT du JSON valide {"subject":string,"body":string}. Le body est en texte simple avec sauts de ligne.'
  const user =
    `Intention du courriel: ${context.intention}\n` +
    (context.clientName ? `Client: ${context.clientName}\n` : '') +
    (context.projet ? `Projet: ${context.projet}\n` : '')
  const raw = await chat(
    [
      { role: 'system', content: sys },
      { role: 'user', content: user },
    ],
    { json: true }
  )
  const p = parseJsonLoose<{ subject?: string; body?: string }>(raw)
  return { subject: String(p.subject || 'Pivot Média'), body: String(p.body || '') }
}

// ---------- Lecture de reçus (vision) ----------
export interface ReceiptData {
  kind?: 'expense' | 'income'
  vendor?: string; project?: string; date?: string; description?: string; category?: string; invoice_number?: string
  subtotal?: number; tps?: number; tvq?: number; total?: number; currency?: string; confidence?: number
}
export const EXPENSE_CATEGORIES = ['équipement', 'logiciels et abonnements', 'déplacement et essence', 'repas et représentation', 'sous-traitance', 'marketing', 'bureau et fournitures', 'télécommunications', 'assurances', 'frais bancaires', 'formation', 'autre']
export const INCOME_CATEGORIES = ['production vidéo', 'photographie', 'montage', 'drone', 'location d’équipement', 'formation', 'subvention', 'autre']
/**
 * Lit la photo d'un document (data URL) : reçu d'achat (dépense) OU facture émise / paiement reçu (revenu).
 * Le modèle indique le type détecté ; les montants sont vérifiés en code. `hint` force le type si connu.
 */
export async function extractReceipt(dataUrl: string, hint?: 'expense' | 'income', filename = 'document'): Promise<ReceiptData> {
  const prompt = `Tu lis un document comptable (Québec, Canada) pour une petite entreprise de production vidéo nommée « Pivot Média » (9535-6887 Québec inc.).
Détermine d'abord s'il s'agit d'une DÉPENSE (reçu ou facture d'un fournisseur que Pivot Média a payé) ou d'un REVENU (facture émise PAR Pivot Média à un client, confirmation de paiement reçu, virement, chèque, subvention).
Règle : si l'émetteur du document est Pivot Média, c'est un REVENU et vendor = le nom du client facturé (destinataire), jamais « Pivot Média ».${hint ? ` Indice : l'utilisateur dit que c'est ${hint === 'income' ? 'un REVENU' : 'une DÉPENSE'}.` : ''}
Retourne UNIQUEMENT un objet JSON avec ces clés :
kind ("expense" ou "income"),
vendor (pour une dépense : nom du commerce ; pour un revenu : nom du client ou payeur), project (titre du projet ou de la prestation si mentionné, sinon ""),
date (AAAA-MM-JJ, date de la transaction ou du paiement), description (courte), invoice_number (numéro de facture s'il y en a un, sinon ""),
category (dépense : une valeur parmi ${EXPENSE_CATEGORIES.join(', ')} ; revenu : une valeur parmi ${INCOME_CATEGORIES.join(', ')}),
subtotal (montant avant taxes), tps (TPS/GST 5 %), tvq (TVQ/QST 9,975 %), total (montant final), currency, confidence (0 à 1).
Si une taxe n'apparaît pas, mets 0. Nombres en décimal avec un point, sans symbole.`
  // PDF : envoyé tel quel (OpenRouter le transmet au modèle, Claude/Gemini lisent les PDF nativement) ; image : vision
  const part = dataUrl.startsWith('data:application/pdf') ? { type: 'file', file: { filename, file_data: dataUrl } } : { type: 'image_url', image_url: { url: dataUrl } }
  const raw = await chat([{ role: 'user', content: [{ type: 'text', text: prompt }, part] }], { json: true, temperature: 0 })
  const d = parseJsonLoose<ReceiptData>(raw || '{}')
  d.kind = d.kind === 'income' ? 'income' : 'expense'
  const num = (v: any) => (v === null || v === undefined || v === '' || isNaN(Number(v))) ? undefined : Math.round(Number(v) * 100) / 100
  d.subtotal = num(d.subtotal); d.tps = num(d.tps); d.tvq = num(d.tvq); d.total = num(d.total)
  // Cohérence : si le total manque, on le recompose ; si le sous-total manque, on le déduit
  if (d.total === undefined && d.subtotal !== undefined) d.total = Math.round(((d.subtotal) + (d.tps || 0) + (d.tvq || 0)) * 100) / 100
  if (d.subtotal === undefined && d.total !== undefined) d.subtotal = Math.round((d.total - (d.tps || 0) - (d.tvq || 0)) * 100) / 100
  if (d.date && !/^\d{4}-\d{2}-\d{2}$/.test(d.date)) delete d.date
  return d
}
